import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  TextField,
  Stack,
  Typography,
  Divider,
  Button,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { AddCircleOutlined, RemoveCircle } from "@mui/icons-material";
import {
  StructureSetEntry,
  DensityOverrideSerialised,
} from "@providers/hop-ord-server/api";

const DENSITY_OVERRIDE_SUBTITLE =
  "Map contoured structures to the assigned material to set the selected density";

export interface DensityOverrideMapping {
  id: string;
  material: string;
  structure: string;
}

interface DensityOverrideSectionProps {
  availableStructures: StructureSetEntry[];
  availableMaterials: DensityOverrideSerialised[];
  densityOverrideState: DensityOverrideMapping[];
  setDensityOverrideState: (overrides: DensityOverrideMapping[]) => void;
  showDensityOverrideError?: boolean;
}

const DensityOverride = ({
  availableStructures,
  availableMaterials,
  densityOverrideState,
  setDensityOverrideState,
  showDensityOverrideError,
}: DensityOverrideSectionProps): JSX.Element => {
  const [densityOverrideError, setDensityOverrideError] = useState("");
  const hasAppliedPreMapRef = useRef(false);

  const structureOptions = useMemo(
    () => availableStructures.map((structure) => structure.name),
    [availableStructures],
  );

  const materialOptions = useMemo(
    () => availableMaterials.map((material) => material.name),
    [availableMaterials],
  );

  // Get available structure options for a specific row (excludes structures already selected in other rows)
  const getAvailableStructureOptions = (currentId: string) => {
    const selectedStructures = densityOverrideState
      .filter((item) => item.id !== currentId && item.structure)
      .map((item) => item.structure);
    return structureOptions.filter(
      (option) => !selectedStructures.includes(option),
    );
  };

  const handleAddDensityOverrideRow = () => {
    // Only add a new row if all existing rows are complete
    const hasIncompleteRow = densityOverrideState.some(
      (item) => !item.structure || !item.material,
    );
    if (hasIncompleteRow) {
      setDensityOverrideError(
        "Please complete or remove the incomplete mapping before adding a new one.",
      );
      return;
    }
    setDensityOverrideError("");
    setDensityOverrideState([
      ...densityOverrideState,
      {
        id: "",
        material: "",
        structure: "",
      },
    ]);
  };

  const handleUpdateDensityOverride = (
    id: string,
    field: "structure" | "material",
    value: string,
  ) => {
    setDensityOverrideError("");
    setDensityOverrideState(
      densityOverrideState.map((item) => {
        if (item.id !== id) return item;
        const updatedItem = { ...item, [field]: value };
        // Update ID to structure-material format when both fields are filled
        if (updatedItem.structure && updatedItem.material) {
          updatedItem.id = `${updatedItem.structure}-${updatedItem.material}`;
        }
        return updatedItem;
      }),
    );
  };

  const handleRemoveDensityOverride = (id: string) => {
    setDensityOverrideError("");
    setDensityOverrideState(
      densityOverrideState.filter((item) => item.id !== id),
    );
  };

  /*
  Pre-map density overrides using material regex patterns.
  For each material, use its regex to match against available structures.
  If a match is found, create a density override mapping.
  One-to-one mapping: each structure can only be mapped once.
  Only runs once to avoid overwriting user edits.
  */
  useEffect(() => {
    // Guard: only apply pre-mapping once
    if (hasAppliedPreMapRef.current) {
      return;
    }

    // Guard: wait for async data to load
    if (availableMaterials.length === 0 || availableStructures.length === 0) {
      return;
    }

    // Guard: don't overwrite existing user/saved mappings
    if (densityOverrideState.length > 0) {
      hasAppliedPreMapRef.current = true;
      return;
    }

    const preMappedOverrides: DensityOverrideMapping[] = [];
    const mappedStructures = new Set<string>();

    availableMaterials.forEach((material) => {
      if (!material.regex) {
        return;
      }

      const matchedStructures = availableStructures.filter((structure) => {
        // Skip if structure is already mapped (one-to-one)
        if (mappedStructures.has(structure.name)) {
          return false;
        }
        try {
          return new RegExp(material.regex, "gi").test(structure.name);
        } catch {
          return false;
        }
      });

      matchedStructures.forEach((structure) => {
        preMappedOverrides.push({
          id: `${structure.name}-${material.name}`,
          material: material.name,
          structure: structure.name,
        });
        mappedStructures.add(structure.name);
      });
    });

    // Only apply and mark as done if we found matches
    if (preMappedOverrides.length > 0) {
      setDensityOverrideState(preMappedOverrides);
      hasAppliedPreMapRef.current = true;
    }
  }, [
    availableMaterials,
    availableStructures,
    densityOverrideState,
    setDensityOverrideState,
  ]);

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1">Density Override</Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
        }}
      >
        {DENSITY_OVERRIDE_SUBTITLE}
      </Typography>
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Stack data-testid="density-override-list">
          {densityOverrideState.map((override) => {
            const isIncomplete =
              (override.structure && !override.material) ||
              (!override.structure && override.material);
            const isEmpty = !override.structure && !override.material;
            const showAddError =
              !!densityOverrideError && (isIncomplete || isEmpty);
            const showSubmitError = !!showDensityOverrideError && isIncomplete;
            return (
              <Stack key={override.id}>
                <Stack
                  direction="row"
                  sx={{
                    gap: 2,
                    alignItems: "flex-start",
                    mt: 1,
                  }}
                >
                  <Autocomplete
                    id={`density-structure-${override.id}`}
                    data-testid={`density-structure-${override.id}`}
                    options={getAvailableStructureOptions(override.id)}
                    fullWidth
                    size="small"
                    value={override.structure || null}
                    onChange={(_, newValue) =>
                      handleUpdateDensityOverride(
                        override.id,
                        "structure",
                        newValue || "",
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Structure"
                        error={
                          !!(
                            (showAddError && !override.structure) ||
                            (showSubmitError && !override.structure)
                          )
                        }
                      />
                    )}
                  />
                  <Autocomplete
                    id={`density-material-${override.id}`}
                    data-testid={`density-material-${override.id}`}
                    options={materialOptions}
                    fullWidth
                    size="small"
                    value={override.material || null}
                    onChange={(_, newValue) =>
                      handleUpdateDensityOverride(
                        override.id,
                        "material",
                        newValue || "",
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Material"
                        error={
                          !!(
                            (showAddError && !override.material) ||
                            (showSubmitError && !override.material)
                          )
                        }
                      />
                    )}
                  />
                  <IconButton
                    onClick={() => handleRemoveDensityOverride(override.id)}
                    data-testid={`remove-density-override-${override.id}`}
                    sx={{ color: "primary.main" }}
                  >
                    <RemoveCircle />
                  </IconButton>
                </Stack>
                {(showAddError || showSubmitError) && (
                  <FormHelperText error>
                    Please complete or remove this mapping
                  </FormHelperText>
                )}
              </Stack>
            );
          })}
        </Stack>
        <Button
          startIcon={<AddCircleOutlined />}
          onClick={handleAddDensityOverrideRow}
          data-testid="add-density-override"
          sx={{ alignSelf: "flex-start" }}
        >
          Add structure
        </Button>
      </Stack>
    </Box>
  );
};

export default DensityOverride;
